/**
 * RunAI OS — Chat Bar
 * Mic + Input + Speaker + Send
 * Yazınca floating bubble gösterir (koyu → soluk, hover'da koyulaşır)
 */
import axios from 'axios'
import { Maximize2, Mic, MicOff, Send, Volume2, VolumeX } from 'lucide-react'
import { Suspense, useRef, useState } from 'react'
import { API_URL } from '../../../config/api'
import { useWindowManager } from '../WindowManager/WindowManagerContext'
import { WINDOW_REGISTRY } from '../WindowManager/windowRegistry'
import { useSpeechInput } from './useSpeechInput'
import { useTTS } from './useTTS'

interface Bubble { role: 'user' | 'assistant'; content: string }

export default function ChatBar() {
  const [message, setMessage] = useState('')
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const { openWindow, windows } = useWindowManager()

  const { listening, toggle: toggleMic } = useSpeechInput((text) => {
    setMessage(prev => prev + text)
    inputRef.current?.focus()
  })

  const { enabled: ttsEnabled, speaking, speak, toggle: toggleTTS } = useTTS()

  const sendMessage = async () => {
    const text = message.trim()
    if (!text) { return }
    setMessage('')
    setBubbles(prev => [...prev.slice(-3), { role: 'user', content: text }])

    const token = localStorage.getItem('token')
    if (!token) { return }
    try {
      const res = await axios.post(
        `${API_URL}/api/chat/send`,
        { agent_id: 1, message: text },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const reply = (res.data as { response?: string }).response ?? ''
      setBubbles(prev => [...prev.slice(-3), { role: 'assistant', content: reply }])
      speak(reply)
    } catch {
      setBubbles(prev => [...prev.slice(-3), { role: 'assistant', content: 'Connection failed.' }])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendMessage() }
  }

  const iconBtn = (active: boolean, color = '#6366f1'): React.CSSProperties => ({
    width: '36px', height: '36px', borderRadius: '10px',
    background: active ? `${color}20` : 'var(--os-input-bg)',
    border: `1px solid ${active ? color : 'var(--os-border)'}`,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: active ? color : 'var(--os-muted)', transition: 'all 0.15s', flexShrink: 0,
  })

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      {/* Floating bubbles */}
      {bubbles.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '16px', right: '16px',
          marginBottom: '6px', display: 'flex', flexDirection: 'column', gap: '5px',
          maxHeight: '180px', overflowY: 'auto', pointerEvents: 'none',
        }}>
          {bubbles.map((b, i) => {
            const isLast = i === bubbles.length - 1
            return (
              <div
                key={i}
                style={{
                  alignSelf: b.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '55%', padding: '7px 11px',
                  borderRadius: b.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                  background: b.role === 'user' ? 'rgba(99,102,241,0.88)' : 'var(--os-surface)',
                  border: '1px solid var(--os-border)',
                  backdropFilter: 'blur(12px)',
                  fontSize: '11px', lineHeight: '1.5',
                  color: b.role === 'user' ? '#fff' : 'var(--os-text)',
                  opacity: isLast ? 1 : 0.45,
                  transition: 'opacity 0.2s',
                  pointerEvents: 'all', cursor: 'default',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1' }}
                onMouseLeave={e => { if (!isLast) { e.currentTarget.style.opacity = '0.45' } }}
              >
                {b.content}
              </div>
            )
          })}
        </div>
      )}

      {/* Bar */}
      <div style={{
        height: '56px', background: 'var(--os-mb-bg)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid var(--os-border)',
        display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px',
      }}>
        <button onClick={toggleMic} title={listening ? 'Stop' : 'Voice'} style={iconBtn(listening, '#10b981')}>
          {listening ? <MicOff size={16} /> : <Mic size={16} />}
        </button>

        <input
          ref={inputRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Chat with your agents..."
          style={{
            flex: 1, padding: '9px 14px',
            background: 'var(--os-input-bg)', border: '1px solid var(--os-border)',
            borderRadius: '10px', color: 'var(--os-text)', fontSize: '12px', outline: 'none',
            transition: 'border 0.2s',
          }}
          onFocus={e => { e.target.style.borderColor = 'rgba(99,102,241,0.5)' }}
          onBlur={e => { e.target.style.borderColor = 'var(--os-border)' }}
        />

        <button onClick={toggleTTS} title={ttsEnabled ? 'Disable voice' : 'Enable voice'} style={iconBtn(ttsEnabled || speaking, '#a855f7')}>
          {ttsEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>

        {/* Expand to window */}
        <button
          onClick={() => {
            const ChatComponent = WINDOW_REGISTRY['/dashboard/chat']
            if (!ChatComponent) { return }
            const isOpen = windows.some(w => w.id === 'chat')
            if (isOpen) { return }
            const vw = window.innerWidth
            const vh = window.innerHeight
            const w = Math.min(700, Math.floor(vw * 0.85))
            const h = Math.min(560, Math.floor(vh * 0.80))
            const WrappedComponent = () => (
              <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--os-muted)', fontSize: '13px' }}>Loading...</div>}>
                <ChatComponent />
              </Suspense>
            )
            openWindow({ id: 'chat', title: 'Chat', component: WrappedComponent, position: { x: Math.floor((vw - w) / 2), y: Math.floor((vh - h) / 2) }, size: { width: w, height: h } })
          }}
          title="Open Chat Window"
          style={iconBtn(windows.some(w => w.id === 'chat'), '#6366f1')}
        >
          <Maximize2 size={16} />
        </button>

        <button
          onClick={() => void sendMessage()}
          disabled={!message.trim()}
          style={{
            ...iconBtn(false),
            background: message.trim() ? '#6366f1' : 'var(--os-input-bg)',
            border: 'none',
            color: message.trim() ? '#fff' : 'var(--os-dim)',
            cursor: message.trim() ? 'pointer' : 'default',
          }}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}
